import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { SceneSet } from '@bighealth/types';
import {
  JumpToSceneSetQueryParams,
  JumpToSceneSetSources,
} from '@bighealth/types/dist/enums';

import { useSafeParams } from 'components/Routes/useSafeParams';
import { useQuery } from 'lib/router/useQuery';
import { setPlayerStatusReady } from 'state/player/actions';

import { useQueryDownloadAssetsForSceneSet } from './useQueryDownloadAssetsForSceneSet';
import { useQueryJumpToSceneSet } from './useQueryJumpToSceneSet';
import { useQuerySceneSetGraphsBulk } from './useQuerySceneSetGraphsBulk';

type QueryState =
  | {
      isLoading: true;
      isSuccess: false;
      isError: false;
      error: undefined;
    }
  | {
      isLoading: false;
      isSuccess: true;
      isError: false;
      error: undefined;
    }
  | {
      isLoading: false;
      isSuccess: false;
      isError: true;
      error: string;
    };

export type QueryAllDataForSceneSetResponse = QueryState & {
  data: {
    status_code: 200 | 500;
    result: {
      id: number;
      sceneSet: SceneSet;
    } | null;
  };
};

/**
 * Returns aggregated information about the Scene Set Graph, Scene Set, and Asset states for the current Scene Set
 * @returns {QueryAllDataForSceneSetResponse} Parsed Jump To Scene Set Response
 */
export const useQueryAllDataForSceneSet = (): QueryAllDataForSceneSetResponse => {
  const { sceneSetId } = useSafeParams();
  const dispatch = useDispatch();
  const query = useQuery();
  const source = query.has(JumpToSceneSetQueryParams.BypassSceneSetValidation)
    ? JumpToSceneSetSources.BypassSceneSetValidation
    : undefined;
  const sceneSetResponse = useQueryJumpToSceneSet(sceneSetId, {
    source: source,
  });
  const sceneSetGraphResponse = useQuerySceneSetGraphsBulk();
  const sceneSetJSON = sceneSetResponse.data?.result?.scene_set_json;
  const downloadResponse = useQueryDownloadAssetsForSceneSet({
    sceneSetJSON,
    enabled: !!sceneSetJSON,
  });
  const isSuccess =
    sceneSetGraphResponse.isSuccess &&
    sceneSetResponse.isSuccess &&
    downloadResponse.isSuccess;
  const isLoading =
    sceneSetGraphResponse.isLoading ||
    sceneSetResponse.isLoading ||
    downloadResponse.isLoading;
  const isError =
    sceneSetGraphResponse.isError ||
    sceneSetResponse.isError ||
    downloadResponse.isError;
  useEffect(() => {
    if (isSuccess) {
      dispatch(setPlayerStatusReady());
      return;
    }
  }, [dispatch, isSuccess]);
  return useMemo(
    () => ({
      isLoading,
      isSuccess,
      isError,
      error: isError
        ? 'Something went wrong downloading SceneSet data and assets'
        : undefined,
      data: sceneSetResponse.data
        ? {
            status_code: 200, // We're approximating a response here. It doesn't matter which currently
            result: {
              id: sceneSetResponse.data?.result?.id,
              sceneSet: sceneSetJSON as SceneSet,
            },
          }
        : undefined,
    }),
    [isError, isLoading, isSuccess, sceneSetJSON, sceneSetResponse.data]
  ) as QueryAllDataForSceneSetResponse;
};
